/**
 * Shared client for the b2b-event-hub (Strapi v5) backend.
 *
 * - One place for the base URL and the JWT header.
 * - Lists go through the hub's custom `GET /api/<plural>/list` endpoints
 *   (page, pageSize ≤ 100, sort, search) which return published entries with `documentId`.
 * - A 401 response triggers the `AUTH_EXPIRED_EVENT` so the app can log the user out.
 * - Backend errors are English; `toUserMessage` maps them to Czech texts for the UI.
 */
import axios, { AxiosError } from "axios";
import { MediaItem, PaginationMeta } from "../types/objects";

export const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";

/** Maximum page size accepted by the hub list endpoints. */
export const MAX_PAGE_SIZE = 100;

export const AUTH_EXPIRED_EVENT = "b2b:auth-expired";

export type ListResource = "contacts" | "contracts" | "crmdocuments" | "usermessages";

export interface ListParams {
    page?: number;
    pageSize?: number;
    sort?: string;
    search?: string;
    [extra: string]: string | number | undefined;
}

export interface ListResponse<T> {
    data: T[];
    meta: { pagination: PaginationMeta; sort?: { field: string; direction: "asc" | "desc" } };
}

export const getToken = (): string | null => {
    try {
        return localStorage.getItem("token");
    } catch {
        return null;
    }
};

export const api = axios.create({ baseURL: `${STRAPI_URL}/api` });

api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers = config.headers ?? {};
        (config.headers as any).Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        const isLogin = String(error.config?.url ?? "").startsWith("/auth/");
        if (error.response?.status === 401 && !isLogin && getToken()) {
            window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
        }
        return Promise.reject(error);
    }
);

/** Builds query params for a list call; empty values are omitted. */
export function buildListQuery(params: ListParams = {}): Record<string, string> {
    const query: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || String(value).trim() === "") continue;
        query[key] = key === "pageSize" ? String(Math.min(Number(value), MAX_PAGE_SIZE)) : String(value).trim();
    }
    return query;
}

export async function fetchList<T>(resource: ListResource, params: ListParams = {}): Promise<ListResponse<T>> {
    const res = await api.get<ListResponse<T>>(`/${resource}/list`, { params: buildListQuery(params) });
    return res.data;
}

/** Loads every page of a list (used by reports). Stops at `maxItems` to protect the browser. */
export async function fetchAll<T>(resource: ListResource, params: ListParams = {}, maxItems = 5000) {
    const items: T[] = [];
    let page = 1;
    let total = 0;
    for (;;) {
        const res = await fetchList<T>(resource, { ...params, page, pageSize: MAX_PAGE_SIZE });
        items.push(...res.data);
        total = res.meta.pagination.total;
        if (page >= res.meta.pagination.pageCount || items.length >= maxItems || res.data.length === 0) break;
        page += 1;
    }
    return { items, total, truncated: total > items.length };
}

export async function createEntry<T = any>(resource: ListResource, data: Record<string, unknown>): Promise<T> {
    const res = await api.post(`/${resource}`, { data });
    return res.data?.data;
}

export async function updateEntry<T = any>(resource: ListResource, documentId: string, data: Record<string, unknown>): Promise<T> {
    const res = await api.put(`/${resource}/${documentId}`, { data });
    return res.data?.data;
}

export async function deleteEntry(resource: ListResource, documentId: string): Promise<void> {
    await api.delete(`/${resource}/${documentId}`);
}

/** Uploads one file to the media library and returns its id. */
export async function uploadFile(file: File): Promise<number> {
    const form = new FormData();
    form.append("files", file);
    const res = await api.post("/upload", form);
    const id = res.data?.[0]?.id;
    if (!id) throw new Error("Upload response does not contain a file id");
    return id;
}

/** Absolute URL of a media file (S3/CDN URLs are already absolute). */
export function mediaUrl(file: Pick<MediaItem, "url"> | null | undefined): string | null {
    if (!file?.url) return null;
    return /^https?:\/\//.test(file.url) ? file.url : `${STRAPI_URL}${file.url}`;
}

/** Maps any API/network error to a Czech message for the user. */
export function toUserMessage(error: unknown, fallback = "Operace se nezdařila. Zkuste to prosím znovu."): string {
    const err = error as AxiosError<any>;
    if (!err || !(err as any).isAxiosError) return fallback;
    if (!err.response) return "Server je nedostupný. Zkontrolujte připojení k internetu.";
    const status = err.response.status;
    const message: string = err.response.data?.error?.message ?? "";
    if (status === 401) return "Přihlášení vypršelo. Přihlaste se prosím znovu.";
    if (status === 403) return "K této akci nemáte oprávnění.";
    if (status === 404) return "Záznam nebyl nalezen – mohl být mezitím smazán.";
    if (status === 413) return "Soubor je příliš velký.";
    if (status === 400 && /unique/i.test(message)) return "Záznam se stejnými údaji již existuje.";
    if (status === 400 && /email/i.test(message)) return "E-mail nemá platný formát.";
    if (status === 400) return "Server odmítl neplatná data. Zkontrolujte vyplněná pole.";
    if (status >= 500) return "Na serveru došlo k chybě. Zkuste to prosím později.";
    return fallback;
}
